import dayjs from 'dayjs';

export const formatDate = (date: string | Date | null | undefined): string => {
  if (!date) return '-';
  return dayjs(date).format('DD.MM.YYYY');
};

export const formatDateTime = (date: string | Date | null | undefined): string => {
  if (!date) return '-';
  return dayjs(date).format('DD.MM.YYYY HH:mm');
};

/**
 * Format like `1 May, 2026 | 12:24` for the "Ro'yxatdan o'tgan" column.
 * 24-hour clock, English month name, no leading zero on day.
 */
export const formatRegisteredAt = (
  date: string | Date | null | undefined,
): string => {
  if (!date) return '-';
  return dayjs(date).format('D MMMM, YYYY | HH:mm');
};

/**
 * Resolve the colour to render the call-phone icon in the admin list:
 * if the operator manually tagged the user, that wins. Otherwise default
 * to green when ≥ 1 call note exists, grey when none.
 */
export const resolveCallIconColor = (
  callIconColor: string | null | undefined,
  callNotesCount: number | undefined,
): string => {
  const palette: Record<string, string> = {
    red: '#EF4444',
    green: '#10B981',
    blue: '#3B82F6',
    yellow: '#F59E0B',
    gray: '#BDC3C7',
  };
  if (callIconColor && palette[callIconColor]) return palette[callIconColor];
  return (callNotesCount ?? 0) > 0 ? '#10B981' : '#BDC3C7';
};

export const formatCurrency = (value: number | null | undefined): string => {
  if (value == null) return '-';
  return new Intl.NumberFormat('uz-UZ').format(value) + " so'm";
};

export const formatRelativeTime = (date: string | Date): string => {
  const diff = dayjs().diff(dayjs(date), 'minute');
  if (diff < 1) return 'Hozirgina';
  if (diff < 60) return `${diff} daqiqa oldin`;
  const hours = Math.floor(diff / 60);
  if (hours < 24) return `${hours} soat oldin`;
  const days = Math.floor(hours / 24);
  return `${days} kun oldin`;
};

// Status color maps
export const tripStatusColors: Record<string, string> = {
  REQUESTED: 'orange',
  ACCEPTED: 'blue',
  DRIVER_ARRIVED: 'purple',
  PICKED_UP: 'cyan',
  ON_THE_WAY: 'green',
  IN_PROGRESS: 'green',
  COMPLETED: 'green',
  CANCELLED: 'red',
  INTERRUPTED: 'red',
  NO_SHOW: 'volcano',
};

export const tripStatusLabels: Record<string, string> = {
  REQUESTED: "So'ralgan",
  ACCEPTED: 'Qabul qilingan',
  DRIVER_ARRIVED: 'Haydovchi yetib keldi',
  PICKED_UP: 'Olib ketildi',
  ON_THE_WAY: "Yo'lda",
  IN_PROGRESS: 'Jarayonda',
  COMPLETED: 'Yakunlangan',
  CANCELLED: 'Bekor qilingan',
  INTERRUPTED: "To'xtatilgan",
  NO_SHOW: 'Kelmadi',
};

export const userStatusColors: Record<string, string> = {
  ONLINE: 'green',
  OFFLINE: 'default',
  BLOCKED: 'red',
  RESTING: 'orange',
};

export const tripTypeLabels: Record<string, string> = {
  INTERCITY_TAXI: 'Shahar ichida',
  INTERREGION_TAXI: 'Shaharlararo',
  SHAHARLAR_ARO: 'Viloyat ichida',
  YOL_YOLAKAY: 'Savdolashish',
  POSTAGE: 'Pochta',
  CARGO: 'Yuk',
};

export const tripTypeColors: Record<string, string> = {
  INTERCITY_TAXI: 'blue',
  INTERREGION_TAXI: 'purple',
  SHAHARLAR_ARO: 'cyan',
  YOL_YOLAKAY: 'geekblue',
  POSTAGE: 'orange',
  CARGO: 'volcano',
};

export const paymentMethodLabels: Record<string, string> = {
  CASH: 'Naqd',
  UZCARD: 'UzCard',
  HUMO: 'Humo',
};

export const numberFormatter = (value: number | string | undefined) =>
  `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');

export const numberParser = (value: string | undefined) =>
  value ? value.replace(/\s/g, '') : '';

// TODO: T-047 - Table column customization (toggle columns, save to localStorage)
